/**
 * Web accessibility props for a toggle chip (a multi/single-select «pill» that
 * flips a selected state on press) — CORE-339.
 *
 * Two RN Web 0.21 traps this exists to close, both reproduced by QA on
 * /dashboard#media (comment 52401):
 *
 *  1. `accessibilityState={{ selected }}` is NOT translated into any real DOM
 *     attribute. In the accessibility tree the chip stays a plain button with
 *     no selected-state — the screen reader never learns it is chosen. The fix
 *     is to emit the actual web attribute (`aria-pressed`) ourselves.
 *
 *  2. Keyboard on a Touchable is half-dead: Space never reaches `onPress`, and
 *     the browser scrolls the page instead. Touchable installs its own
 *     *bubbling* `onKeyDown` after our props, so a plain `onKeyDown` prop is
 *     overwritten. We therefore hook the *capture* phase (`onKeyDownCapture`),
 *     which runs first, `preventDefault()` Space so it no longer scrolls, and
 *     `stopPropagation()` so Enter is not additionally re-fired by Touchable's
 *     own handler (which would toggle twice). Same technique as the roving
 *     tabs in AllAchievementsModal.
 *
 * The returned object is spread straight onto the `<TouchableOpacity>`; keep the
 * component's own `onPress` for mouse/tap. On native only the RN accessibility
 * props are returned, so no web attribute leaks into the native bundle.
 */

const ACTIVATION_KEYS = new Set([' ', 'Spacebar', 'Enter']);

/** True for the keys that must activate a chip (Enter or Space). */
export function isChipActivationKey(key: unknown): boolean {
  return typeof key === 'string' && ACTIVATION_KEYS.has(key);
}

/**
 * Keyboard handler for the capture phase. Returns `true` when it consumed the
 * event (so callers/tests can assert activation happened).
 */
export function handleChipActivationKey(event: any, onToggle: () => void): boolean {
  if (!event || !isChipActivationKey(event.key)) return false;
  event.preventDefault?.();
  event.stopPropagation?.();
  onToggle();
  return true;
}

export interface ToggleChipProps {
  accessibilityRole: 'button';
  accessibilityState: { selected: boolean };
  role?: 'button';
  'aria-pressed'?: boolean;
  'aria-label'?: string;
  onKeyDownCapture?: (event: any) => void;
}

/**
 * Build the props to spread onto a toggle chip's `<TouchableOpacity>`.
 *
 * @param platformOS  `Platform.OS` — web attributes are only emitted on 'web'.
 * @param selected    whether the chip is currently chosen.
 * @param onToggle    fired on Enter/Space (mirror of the chip's `onPress`).
 * @param label       optional accessible name (defaults to the chip's text).
 */
export function buildToggleChipProps(
  platformOS: string,
  selected: boolean,
  onToggle: () => void,
  label?: string,
): ToggleChipProps {
  const props: ToggleChipProps = {
    accessibilityRole: 'button',
    accessibilityState: { selected },
  };
  if (platformOS === 'web') {
    props.role = 'button';
    props['aria-pressed'] = selected;
    if (label) props['aria-label'] = label;
    props.onKeyDownCapture = (event: any) => handleChipActivationKey(event, onToggle);
  }
  return props;
}
